import _, { isArray, isEmpty, keys } from 'lodash';
import { AnyObject } from 'yup/lib/object';
import { ProductConfigurationResponse } from '../../api/ConfigApi';
import {
	EndorsementExposureUpdateRequest,
	EndorsementResponse,
	EndorsementUpdateRequest,
	EndorsementUpdateRequestHelpers,
	EndorsementUpdateRequestWithHelpers,
} from '../../types/endorsements';
import {
	Exposure,
	ExposureCreateRequest,
	ExposureCreateRequestHelpers,
	FieldGroupCreateRequest,
	FieldGroupsByLocatorMap,
	FieldGroupUpdateRequest,
	FieldValuesMap,
	Peril,
	PerilCharacteristics,
	PerilCreateRequest,
	PerilCreateRequestHelpers,
	PerilUpdateRequest,
	Policy2,
} from '../../types/policy';
import {
	RenewalExposureUpdateRequest,
	RenewalExposureUpdateRequestWithHelpers,
	RenewalResponse,
	RenewalUpdateRequest,
	RenewalUpdateRequestHelpers,
} from '../../types/renewals';
import {
	formatDateStringShort,
	getCharacteristicAtTime_T_,
	getPerilCharacteristicAtTime_T_,
	isTimestampWithinExclusiveRange,
} from '../../util/util';
import { filterObjectByKeys } from '../fastForm/utils';

export function removePendingRemoveFieldGroupsLocatorsFromFieldValues(
	pendingRemoveFieldGroups: string[],
	fieldValues: FieldValuesMap,
) {
	if (pendingRemoveFieldGroups?.length) {
		// FIXME Searching every attribute because there is no data about which field name is being deleted.
		Object.keys(fieldValues).forEach((fieldName) => {
			if (Array.isArray(fieldValues[fieldName])) {
				// filter out all locators mentioned in pendingRemoveFieldGroups
				fieldValues[fieldName] = fieldValues[fieldName].filter((locator) => {
					return !pendingRemoveFieldGroups.includes(locator);
				});
			}
		});
	}
	return fieldValues;
}

export function updateFieldGroupsByLocatorWithPendingChanges(
	fieldGroupsByLocator: FieldGroupsByLocatorMap,
	pendingUpdateFieldGroups: FieldGroupUpdateRequest[],
	pendingRemoveFieldGroups: string[],
) {
	const allFieldGroupsByLocator: FieldGroupsByLocatorMap = Object.keys(
		fieldGroupsByLocator,
	).reduce((acc, fieldGroupLocator) => {
		const removedLocator = pendingRemoveFieldGroups.find(
			(removedLoc) => removedLoc === fieldGroupLocator,
		);

		// multiple pendingUpdateFieldGroups can satisfy this condition
		const pendingUpdateFieldGroupsWithSameFieldGroupLocator =
			pendingUpdateFieldGroups.filter(
				(pufg) => pufg.fieldGroupLocator === fieldGroupLocator,
			);

		// skip if field group is removed.
		if (!removedLocator) {
			let fieldValues: FieldValuesMap = {};
			if (
				pendingUpdateFieldGroupsWithSameFieldGroupLocator &&
				pendingUpdateFieldGroupsWithSameFieldGroupLocator.length
			) {
				pendingUpdateFieldGroupsWithSameFieldGroupLocator.forEach((pufg) => {
					fieldValues = {
						...fieldValues,
						...(pufg.fieldValues || {}),
					};
				});
			}
			acc[fieldGroupLocator] = {
				...fieldGroupsByLocator[fieldGroupLocator],
				...fieldValues,
			};
		} else {
			delete acc[removedLocator];
		}

		return acc;
	}, {} as FieldGroupsByLocatorMap);
	return allFieldGroupsByLocator;
}

export function getPerilCharacteristicsRequired(
	selectedPeril: Peril,
	timestamp: string,
): PerilCharacteristics {
	const activePerilCharacteristic: PerilCharacteristics =
		selectedPeril.characteristics.find((c) => {
			return (
				!c.replacedTimestamp &&
				isTimestampWithinExclusiveRange(
					timestamp,
					c.coverageStartTimestamp,
					c.coverageEndTimestamp,
				)
			);
		});
	if (activePerilCharacteristic) {
		return activePerilCharacteristic;
	} else {
		const allActiveCharacteristics = selectedPeril.characteristics.filter(
			(c) => !c.replacedTimestamp,
		);
		const firstActiveAfterTimestamp = allActiveCharacteristics.find(
			(c) => +c.coverageStartTimestamp > parseInt(timestamp, 10),
		);
		const lastActiveBeforeTimestamp = allActiveCharacteristics
			.reverse()
			.find((c) => +c.coverageEndTimestamp <= parseInt(timestamp, 10));
		return firstActiveAfterTimestamp || lastActiveBeforeTimestamp;
	}
}

/**
 * Gets either the added exposure object of type ExposureCreateRequest or the updated exposure object of
 * type EndorsementExposureUpdateRequest | RenewalExposureUpdateRequest or it will create an update object
 * if it does not exist
 */
export function getExposureObject(
	updateObj: EndorsementUpdateRequest | RenewalUpdateRequest,
	exposureLocator: string,
) {
	const addExposures = updateObj.addExposures || [];
	const isAdded = addExposures.find(
		(e) => e.exposureLocator === exposureLocator,
	);

	return isAdded ? isAdded : getUpdatedExposureObj(updateObj, exposureLocator);
}

/**
 * Gets or creates an EndorsementExposureUpdateRequest | RenewalExposureUpdateRequest from updateObj
 * This function is only relevant for existing exposures on endorsements.
 */
export function getUpdatedExposureObj(
	updateObj: EndorsementUpdateRequest | RenewalUpdateRequest,
	exposureLocator: string,
): EndorsementExposureUpdateRequest | RenewalExposureUpdateRequest {
	const existingUpdateObj = findUpdatedExposureObj(updateObj, exposureLocator);
	const newUpdateObj =
		existingUpdateObj || createUpdateExposureObj(exposureLocator);
	return newUpdateObj;
}

/**
 * Get the index of the exposure(either added or updated) object from updateObj
 */
export function getExposureIndexFromContext(
	updateObj: EndorsementUpdateRequest | RenewalUpdateRequest,
	exposureLocator: string,
) {
	const addedExposures = updateObj.addExposures || [];
	const addedExposureIndex = addedExposures.findIndex(
		(e) => e.exposureLocator === exposureLocator,
	);
	if (addedExposureIndex >= 0) {
		return addedExposureIndex;
	} else {
		return getUpdateExposureIndexFromContext(updateObj, exposureLocator);
	}
}

export function getUpdateExposureIndexFromContext(
	updateObj: EndorsementUpdateRequest | RenewalUpdateRequest,
	locator: string,
) {
	const updateExposuresArr = updateObj.updateExposures || [];
	const updateExposureObjInd = updateExposuresArr.findIndex(
		(exp) => exp.exposureLocator === locator,
	);

	return updateExposureObjInd;
}

export function getAddedExposureFromContext(
	updateObj: EndorsementUpdateRequest | RenewalUpdateRequest,
	exposureLocator: string,
) {
	const addedExposures = updateObj.addExposures || [];
	return addedExposures.find((e) => e.exposureLocator === exposureLocator);
}

export function findUpdatedExposureObj(
	updateObj: EndorsementUpdateRequest | RenewalUpdateRequest,
	exposureLocator: string,
) {
	const updatedExposures = updateObj.updateExposures || [];
	return updatedExposures.find((e) => e.exposureLocator === exposureLocator);
}

/**
 * Creates an empty EndorsementExposureUpdateRequest | RenewalExposureUpdateRequest with given exposureLocator
 */
export function createUpdateExposureObj(
	exposureLocator: string,
): EndorsementExposureUpdateRequest | RenewalExposureUpdateRequest {
	return {
		exposureLocator,
		fieldValues: {},
		addFieldGroups: [],
		updateFieldGroups: [],
		removeFieldGroups: [],
		addPerils: [],
		endPerils: [],
		updatePerils: [],
	};
}

/*------------------------------------------------------------------------------------
	UTIL FUNCTIONS FOR GETTING DISPLAY NAMES OF EXPOSURES & PERILS FROM _DESCRIPTION FIELD
	------------------------------------------------------------------------------------*/

export function getExposureName(
	exposure: Exposure | ExposureCreateRequest,
	defaultDisplayName: string,
	selectedRenewal: RenewalResponse,
	endorsement: EndorsementResponse,
) {
	// if exposure does not have characteristics, then it's a create request
	if (!(exposure as Exposure).characteristics) {
		return getAddedExposureDisplayName(
			exposure as ExposureCreateRequest,
			defaultDisplayName,
			selectedRenewal,
			endorsement,
		);
	} else {
		return getUpdatedExposureDisplayName(
			exposure as Exposure,
			defaultDisplayName,
			selectedRenewal,
			endorsement,
		);
	}
}

export function getAddedExposureDisplayName(
	exposure: ExposureCreateRequest,
	defaultDisplayName: string,
	selectedRenewal: RenewalResponse,
	endorsement: EndorsementResponse,
) {
	const entity = selectedRenewal ? selectedRenewal : endorsement;
	const selectedExposure = entity?.addExposures?.find(
		(e) => e.exposureLocator === exposure.exposureLocator,
	);
	if (!selectedExposure) {
		return defaultDisplayName;
	}
	if (
		!!(selectedExposure as ExposureCreateRequest)?.fieldValues?.[
			'_description'
		]?.[0]
	) {
		const description = selectedExposure.fieldValues['_description'];
		return isArray(description) ? description[0] : description;
	} else {
		return defaultDisplayName;
	}
}

export function getUpdatedExposureDisplayName(
	exposure: Exposure,
	defaultDisplayName: string,
	selectedRenewal: RenewalResponse,
	endorsement: EndorsementResponse,
) {
	const entity = selectedRenewal ? selectedRenewal : endorsement;
	// first check selectedRenewal for saved _description field
	const exposureUpdateReq = entity?.updateExposures?.find(
		(e) => e.exposureLocator === exposure.locator,
	);

	if (!exposureUpdateReq) {
		return selectedRenewal
			? getExposureDisplayNameFromRenewalCharacteristics(
					exposure,
					defaultDisplayName,
					selectedRenewal,
			  )
			: getExposureDisplayNameFromEndorsementCharacteristics(
					exposure,
					defaultDisplayName,
					endorsement,
			  );
	}
	// if saved _description field, return value
	if (!!exposureUpdateReq?.fieldValues?.['_description']?.[0]) {
		const description = exposureUpdateReq.fieldValues['_description'];
		return isArray(description) ? description[0] : description;
	}
	return selectedRenewal
		? getExposureDisplayNameFromRenewalCharacteristics(
				exposure,
				defaultDisplayName,
				selectedRenewal,
		  )
		: getExposureDisplayNameFromEndorsementCharacteristics(
				exposure,
				defaultDisplayName,
				endorsement,
		  );
}

export function getExposureDisplayNameFromRenewalCharacteristics(
	exposure: Exposure,
	defaultDisplayName: string,
	selectedRenewal: RenewalResponse,
) {
	// if no _description field updated, get characteristic and check for _description field
	const characteristic = getCharacteristicAtTime_T_(
		exposure.characteristics,
		parseInt(selectedRenewal.renewalStartTimestamp, 10),
	);
	if (!!characteristic?.fieldValues?.['_description']?.[0]) {
		const description = characteristic.fieldValues['_description'];
		return isArray(description) ? description[0] : description;
	} else {
		return defaultDisplayName;
	}
}

export function getExposureDisplayNameFromEndorsementCharacteristics(
	exposure: Exposure,
	defaultDisplayName: string,
	endorsement: EndorsementResponse,
) {
	// if no _description field updated, get characteristic and check for _description field
	const characteristic = getCharacteristicAtTime_T_(
		exposure.characteristics,
		parseInt(endorsement.startTimestamp, 10),
	);
	if (!!characteristic?.fieldValues?.['_description']?.[0]) {
		const description = characteristic.fieldValues['_description'];
		return isArray(description) ? description[0] : description;
	} else {
		return defaultDisplayName;
	}
}

export function getPerilName(
	peril: Peril | PerilCreateRequest,
	defaultDisplayName: string,
	exposure: Exposure | ExposureCreateRequest,
	selectedRenewal: RenewalResponse,
	endorsement: EndorsementResponse,
) {
	if (!(peril as Peril).characteristics) {
		return getAddedPerilDisplayName(
			peril as PerilCreateRequest,
			exposure as Exposure,
			defaultDisplayName,
			selectedRenewal,
			endorsement,
		);
	}
	return getUpdatedPerilDisplayName(
		peril as Peril,
		exposure as Exposure,
		defaultDisplayName,
		selectedRenewal,
		endorsement,
	);
}

export function getAddedPerilDisplayName(
	peril: PerilCreateRequest,
	exposure: Exposure | ExposureCreateRequest,
	defaultDisplayName: string,
	selectedRenewal: RenewalResponse,
	endorsement: EndorsementResponse,
) {
	if (!(exposure as Exposure).characteristics) {
		return getAddedPerilDisplayNameFromAddedExposure(
			peril,
			exposure as ExposureCreateRequest,
			defaultDisplayName,
			selectedRenewal,
			endorsement,
		);
	} else {
		return getAddedPerilDisplayNameFromUpdatedExposure(
			peril,
			exposure as Exposure,
			defaultDisplayName,
			selectedRenewal,
			endorsement,
		);
	}
}

export function getAddedPerilDisplayNameFromAddedExposure(
	peril: PerilCreateRequest,
	exposure: ExposureCreateRequest,
	defaultDisplayName: string,
	selectedRenewal: RenewalResponse,
	endorsement: EndorsementResponse,
) {
	const entity = selectedRenewal ? selectedRenewal : endorsement;
	const selectedExposure = entity?.addExposures?.find(
		(e) =>
			e.exposureLocator === (exposure as ExposureCreateRequest).exposureLocator,
	);
	if (!selectedExposure) {
		return defaultDisplayName;
	}
	const selectedPeril = selectedExposure?.perils?.find(
		(p) => p.locator === peril.locator,
	);
	if (!selectedPeril) {
		return defaultDisplayName;
	}
	// check if added peril has _description field
	if (!!selectedPeril?.fieldValues?.['_description']?.[0]) {
		const description = selectedPeril.fieldValues['_description'];
		return isArray(description) ? description[0] : description;
	}
	// otherwise return default name
	return defaultDisplayName;
}

export function getAddedPerilDisplayNameFromUpdatedExposure(
	peril: PerilCreateRequest,
	exposure: Exposure,
	defaultDisplayName: string,
	selectedRenewal: RenewalResponse,
	endorsement: EndorsementResponse,
) {
	const entity = selectedRenewal ? selectedRenewal : endorsement;
	const selectedExposure = entity?.updateExposures?.find(
		(e) => e.exposureLocator === (exposure as Exposure).locator,
	);
	if (!selectedExposure) {
		return defaultDisplayName;
	}
	const selectedPeril = selectedExposure?.addPerils?.find(
		(p) => p.locator === peril.locator,
	);
	if (!selectedPeril) {
		return defaultDisplayName;
	}
	// check if added peril has _description field
	if (!!selectedPeril?.fieldValues?.['_description']?.[0]) {
		const description = selectedPeril.fieldValues['_description'];
		return isArray(description) ? description[0] : description;
	}
	// otherwise return default name
	return defaultDisplayName;
}

export function getUpdatedPerilDisplayName(
	peril: Peril,
	exposure: Exposure,
	defaultDisplayName: string,
	selectedRenewal: RenewalResponse,
	endorsement: EndorsementResponse,
) {
	const entity = selectedRenewal ? selectedRenewal : endorsement;
	// first check selectedRenewal for saved _description field
	const exposureUpdateReq = entity?.updateExposures?.find(
		(e) => e.exposureLocator === exposure.locator,
	);
	if (!exposureUpdateReq) {
		return selectedRenewal
			? getPerilDisplayNameFromRenewalCharacteristics(
					peril,
					defaultDisplayName,
					selectedRenewal,
			  )
			: getPerilDisplayNameFromEndorsementCharacteristics(
					peril,
					defaultDisplayName,
					endorsement,
			  );
	}
	const perilUpdateReq = exposureUpdateReq?.updatePerils?.find(
		(p) => p.perilLocator === peril.locator,
	);
	if (!perilUpdateReq) {
		return selectedRenewal
			? getPerilDisplayNameFromRenewalCharacteristics(
					peril,
					defaultDisplayName,
					selectedRenewal,
			  )
			: getPerilDisplayNameFromEndorsementCharacteristics(
					peril,
					defaultDisplayName,
					endorsement,
			  );
	}
	// if saved _description field, return value
	if (!!perilUpdateReq?.fieldValues?.['_description']?.[0]) {
		const description = perilUpdateReq.fieldValues['_description'];
		return isArray(description) ? description[0] : description;
	}
	return selectedRenewal
		? getPerilDisplayNameFromRenewalCharacteristics(
				peril,
				defaultDisplayName,
				selectedRenewal,
		  )
		: getPerilDisplayNameFromEndorsementCharacteristics(
				peril,
				defaultDisplayName,
				endorsement,
		  );
}

export function getPerilDisplayNameFromRenewalCharacteristics(
	peril: Peril,
	defaultDisplayName: string,
	selectedRenewal: RenewalResponse,
) {
	// if no _description field updated, get characteristic and check for _description field
	const characteristic = getPerilCharacteristicAtTime_T_(
		peril.characteristics,
		parseInt(selectedRenewal.renewalStartTimestamp, 10),
	);
	if (!!characteristic?.fieldValues?.['_description']?.[0]) {
		const description = characteristic.fieldValues['_description'];
		return isArray(description) ? description[0] : description;
	} else {
		return defaultDisplayName;
	}
}

export function getPerilDisplayNameFromEndorsementCharacteristics(
	peril: Peril,
	defaultDisplayName: string,
	endorsement: EndorsementResponse,
) {
	// if no _description field updated, get characteristic and check for _description field
	const characteristic = getPerilCharacteristicAtTime_T_(
		peril.characteristics,
		parseInt(endorsement.startTimestamp, 10),
	);
	if (!!characteristic?.fieldValues?.['_description']?.[0]) {
		const description = characteristic.fieldValues['_description'];
		return isArray(description) ? description[0] : description;
	} else {
		return defaultDisplayName;
	}
}

export const mergePerilUpdateObjectAndPerilCharacteristics = (
	perilObj: PerilUpdateRequest,
	perilConfig: PerilCharacteristics,
) => {
	// check if limit has been updated
	const iPIUpdated =
		perilObj.indemnityPerItem || perilObj.removeIndemnityPerItem;
	const iPEUpdated =
		perilObj.indemnityPerEvent || perilObj.removeIndemnityPerEvent;
	const iIAUpdated =
		perilObj.indemnityInAggregate || perilObj.removeIndemnityInAggregate;
	const lSUpdated = perilObj.lumpSumPayment || perilObj.removeLumpSumPayment;
	const dUpdated = perilObj.deductible || perilObj.removeDeductible;

	// determine if the value should be based off the update object or from the characteristic
	// if peril is a PerilCreateRequest, always use the perilObj
	const indemnityPerItem =
		iPIUpdated || perilObj.locator
			? perilObj.indemnityPerItem
			: perilConfig.indemnityPerItem;
	const indemnityPerEvent =
		iPEUpdated || perilObj.locator
			? perilObj.indemnityPerEvent
			: perilConfig.indemnityPerEvent;
	const indemnityInAggregate =
		iIAUpdated || perilObj.locator
			? perilObj.indemnityInAggregate
			: perilConfig.indemnityInAggregate;
	const lumpSumPayment =
		lSUpdated || perilObj.locator
			? perilObj.lumpSumPayment
			: perilConfig.lumpSumPayment;
	const deductible =
		dUpdated || perilObj.locator ? perilObj.deductible : perilConfig.deductible;

	return {
		indemnityInAggregate,
		indemnityPerEvent,
		indemnityPerItem,
		lumpSumPayment,
		deductible,
		...perilObj,
	} as PerilUpdateRequest;
};

export const mergeCurrentExposureAddObjectAndExposureAddObject = (
	newUpdateObject: ExposureCreateRequest & ExposureCreateRequestHelpers,
	currentUpdateObject: ExposureCreateRequest,
	locator: string,
) => {
	const fieldValues = mergeFieldValues(
		newUpdateObject?.fieldValues,
		currentUpdateObject?.fieldValues,
	);

	// should hold latest fieldGroups in new update object
	const fieldGroups = mergeAddFieldGroups(
		newUpdateObject?.addFieldGroups,
		currentUpdateObject?.fieldGroups,
		newUpdateObject.allAddFieldGroupsDeleted,
	);

	const mergedUpdate = {
		fieldValues,
		fieldGroups,
		exposureLocator: locator,
		exposureName: currentUpdateObject.exposureName,
		perils: currentUpdateObject.perils,
		locator: currentUpdateObject.locator,
	} as ExposureCreateRequest & ExposureCreateRequestHelpers;
	return mergedUpdate;
};

export const mergeCurrentExposureUpdateObjectAndExposureUpdateObject = (
	newUpdateObject:
		| EndorsementUpdateRequestWithHelpers
		| RenewalExposureUpdateRequestWithHelpers,
	currentUpdateObject:
		| EndorsementUpdateRequestWithHelpers
		| RenewalExposureUpdateRequestWithHelpers,
	locator: string,
) => {
	const addFieldGroups = mergeAddFieldGroups(
		newUpdateObject?.addFieldGroups,
		currentUpdateObject?.addFieldGroups,
		newUpdateObject.allAddFieldGroupsDeleted,
	);

	const updateFieldGroups = mergeFieldGroupUpdates(
		newUpdateObject?.updateFieldGroups,
		currentUpdateObject?.updateFieldGroups,
	);

	const fieldValues = mergeFieldValues(
		newUpdateObject?.fieldValues,
		currentUpdateObject?.fieldValues,
	);

	const removeFieldGroups = mergeRemoveFieldGroups(
		newUpdateObject?.removeFieldGroups,
		currentUpdateObject?.removeFieldGroups,
	);

	const addPerils = currentUpdateObject
		? currentUpdateObject.addPerils || []
		: [];
	const endPerils = currentUpdateObject
		? currentUpdateObject.endPerils || []
		: [];
	const updatePerils = currentUpdateObject
		? currentUpdateObject.updatePerils || []
		: [];
	const mergedUpdate = {
		addFieldGroups,
		updateFieldGroups,
		removeFieldGroups,
		addPerils,
		endPerils,
		updatePerils,
		fieldValues,
		exposureLocator: locator,
	} as
		| (EndorsementExposureUpdateRequest & EndorsementUpdateRequestWithHelpers)
		| (RenewalExposureUpdateRequest & RenewalExposureUpdateRequestWithHelpers);

	// delete any empty arrays
	if (mergedUpdate.addFieldGroups.length === 0) {
		delete mergedUpdate.addFieldGroups;
	}

	return mergedUpdate;
};

export const mergePerilCreateObjects = (
	newUpdateObject: PerilCreateRequest & PerilCreateRequestHelpers,
	currentUpdateObject: PerilCreateRequest,
	locator: string,
) => {
	const fieldValues = mergeFieldValues(
		newUpdateObject?.fieldValues,
		currentUpdateObject?.fieldValues,
	);

	const indemnityUpdates = mergeIndemnities(
		newUpdateObject,
		currentUpdateObject,
	);

	const fieldGroups = mergeAddFieldGroups(
		newUpdateObject?.addFieldGroups,
		currentUpdateObject?.fieldGroups,
		newUpdateObject.allAddFieldGroupsDeleted,
	);

	const filteredIndemnityUpdates =
		filterIndemnitiesForCreatePerilRequest(indemnityUpdates);

	const mergedUpdate = {
		...filteredIndemnityUpdates,
		fieldValues,
		fieldGroups,
		perilLocator: locator,
		locator: locator,
		name: currentUpdateObject.name,
	} as PerilCreateRequest & PerilCreateRequestHelpers;

	if (Object.keys(mergedUpdate.fieldValues).length === 0) {
		delete mergedUpdate.fieldValues;
	}

	return mergedUpdate;
};

export const mergeCurrentPerilUpdateObjectAndPerilUpdateObject = (
	newUpdateObject: PerilUpdateRequest,
	currentUpdateObject: PerilUpdateRequest,
	locator: string,
) => {
	const addFieldGroups = mergeAddFieldGroups(
		newUpdateObject.addFieldGroups,
		currentUpdateObject.addFieldGroups,
		newUpdateObject.allAddFieldGroupsDeleted,
	);

	const updateFieldGroups = mergeFieldGroupUpdates(
		newUpdateObject?.updateFieldGroups,
		currentUpdateObject?.updateFieldGroups,
	);

	const fieldValues = mergeFieldValues(
		newUpdateObject?.fieldValues,
		currentUpdateObject?.fieldValues,
	);

	const removeFieldGroups = mergeRemoveFieldGroups(
		newUpdateObject?.removeFieldGroups,
		currentUpdateObject?.removeFieldGroups,
	);

	const indemnityUpdates = mergeIndemnities(
		newUpdateObject,
		currentUpdateObject,
	);

	const mergedUpdate = {
		...indemnityUpdates,
		addFieldGroups,
		updateFieldGroups,
		removeFieldGroups,
		fieldValues,
		perilLocator: locator,
	} as PerilUpdateRequest;

	if (
		!mergedUpdate.addFieldGroups ||
		mergedUpdate.addFieldGroups?.length === 0
	) {
		delete mergedUpdate.addFieldGroups;
	}
	if (
		!mergedUpdate.updateFieldGroups ||
		mergedUpdate.updateFieldGroups?.length === 0
	) {
		delete mergedUpdate.updateFieldGroups;
	}
	if (
		!mergedUpdate.removeFieldGroups ||
		mergedUpdate.removeFieldGroups?.length === 0
	) {
		delete mergedUpdate.removeFieldGroups;
	}
	if (
		!mergedUpdate.fieldValues ||
		Object.keys(mergedUpdate.fieldValues).length === 0
	) {
		delete mergedUpdate.fieldValues;
	}

	return mergedUpdate;
};

export const mergeCurrentAndUpdateObjects = (
	newUpdateObject:
		| (EndorsementUpdateRequest & EndorsementUpdateRequestHelpers)
		| (RenewalUpdateRequest & RenewalUpdateRequestHelpers),
	currentUpdateObject: EndorsementUpdateRequest | RenewalUpdateRequest,
) => {
	const endExposures = currentUpdateObject.endExposures;
	const addFieldGroups = mergeAddFieldGroups(
		newUpdateObject?.addFieldGroups,
		currentUpdateObject?.addFieldGroups,
		newUpdateObject.allAddFieldGroupsDeleted,
	);

	const updateFieldGroups = mergeFieldGroupUpdates(
		newUpdateObject?.updateFieldGroups,
		currentUpdateObject?.updateFieldGroups,
	);

	const fieldValues = mergeFieldValues(
		newUpdateObject?.fieldValues,
		currentUpdateObject?.fieldValues,
	);

	const removeFieldGroups = mergeRemoveFieldGroups(
		newUpdateObject.removeFieldGroups,
		currentUpdateObject.removeFieldGroups,
	);

	const addExposures = [...currentUpdateObject.addExposures];

	const mergedUpdate = {
		updateExposures: [],
		endExposures,
		addFieldGroups,
		updateFieldGroups,
		removeFieldGroups,
		addExposures,
		fieldValues,
	} as
		| (EndorsementUpdateRequest & EndorsementUpdateRequestHelpers)
		| (RenewalUpdateRequest & RenewalUpdateRequestHelpers);

	if (mergedUpdate?.addFieldGroups?.length === 0) {
		delete mergedUpdate.addFieldGroups;
	}
	if (mergedUpdate?.updateFieldGroups?.length === 0) {
		delete mergedUpdate.updateFieldGroups;
	}
	if (mergedUpdate?.removeFieldGroups?.length === 0) {
		delete mergedUpdate.removeFieldGroups;
	}
	if (Object.keys(mergedUpdate?.fieldValues)?.length === 0) {
		delete mergedUpdate.fieldValues;
	}
	return mergedUpdate;
};

const mergeIndemnities = (
	newUpdateObject: EndorsementUpdateRequest | RenewalUpdateRequest,
	currentUpdateObject: EndorsementUpdateRequest | RenewalUpdateRequest,
) => {
	const filteredNewUpdateObject = newUpdateObject
		? filterObjectByKeys(newUpdateObject, [
				'addFieldGroups',
				'updateFieldGroups',
				'removeFieldGroups',
				'fieldValues',
				'perilLocator',
		  ])
		: {};
	const filteredCurrentUpdateObject = currentUpdateObject
		? filterObjectByKeys(currentUpdateObject, [
				'addFieldGroups',
				'updateFieldGroups',
				'removeFieldGroups',
				'fieldValues',
				'perilLocator',
		  ])
		: {};
	return {
		...filteredCurrentUpdateObject,
		...filteredNewUpdateObject,
	};
};

const mergeRemoveFieldGroups = (
	newRemoveFieldGroups: string[],
	currentRemoveFieldGroups: string[],
) => {
	const mergeRemoveFieldGroups = currentRemoveFieldGroups
		? newRemoveFieldGroups
			? newRemoveFieldGroups.concat(currentRemoveFieldGroups)
			: currentRemoveFieldGroups || []
		: newRemoveFieldGroups || [];
	return mergeRemoveFieldGroups;
};

const mergeFieldValues = (
	newFieldValues: FieldValuesMap,
	currentFieldValues: FieldValuesMap,
) => {
	return currentFieldValues
		? {
				...currentFieldValues,
				...newFieldValues,
		  }
		: newFieldValues || {};
};

const mergeAddFieldGroups = (
	pendingFieldGroupUpdates: FieldGroupCreateRequest[],
	existingFieldGroupUpdates: FieldGroupCreateRequest[],
	allAddFieldGroupsDeleted: { [fieldName: string]: boolean },
) => {
	const mergedFieldGroupUpdates = [] as FieldGroupCreateRequest[];

	// Group updates by field name
	const pendingFieldGroupUpdatesByFieldName = _.groupBy(
		pendingFieldGroupUpdates,
		(pendingFieldGroupUpdate) => pendingFieldGroupUpdate.fieldName,
	);

	const existingFieldGroupUpdatesByFieldName = _.groupBy(
		existingFieldGroupUpdates,
		(existingFieldGroupUpdate) => existingFieldGroupUpdate.fieldName,
	);

	Object.keys({
		...pendingFieldGroupUpdatesByFieldName,
		...existingFieldGroupUpdatesByFieldName,
	}).forEach((fieldName) => {
		if (allAddFieldGroupsDeleted && allAddFieldGroupsDeleted[fieldName]) {
			return;
		} else if (!pendingFieldGroupUpdatesByFieldName[fieldName]) {
			mergedFieldGroupUpdates.push(
				...existingFieldGroupUpdatesByFieldName[fieldName],
			);
		} else if (!existingFieldGroupUpdatesByFieldName[fieldName]) {
			mergedFieldGroupUpdates.push(
				...pendingFieldGroupUpdatesByFieldName[fieldName],
			);
		} else {
			const pendingFieldGroupUpdatesMap = pendingFieldGroupUpdatesByFieldName[
				fieldName
			]
				? pendingFieldGroupUpdatesByFieldName[fieldName].reduce(
						(acc, curr, index) => {
							acc[`${curr.fieldName}-${index}`] = curr;
							return acc;
						},
						{} as { [key: string]: FieldGroupCreateRequest },
				  )
				: ({} as { [key: string]: FieldGroupCreateRequest });

			const existingFieldGroupUpdatesMap = existingFieldGroupUpdatesByFieldName[
				fieldName
			]
				? existingFieldGroupUpdatesByFieldName[fieldName].reduce(
						(acc, curr, index) => {
							acc[`${curr.fieldName}-${index}`] = curr;
							return acc;
						},
						{} as { [key: string]: FieldGroupCreateRequest },
				  )
				: ({} as { [key: string]: FieldGroupCreateRequest });

			const mergedFieldGroupUpdatesMap = {
				...existingFieldGroupUpdatesMap,
				...pendingFieldGroupUpdatesMap,
			};
			// Filter down any removed field groups
			Object.keys(mergedFieldGroupUpdatesMap).forEach((key) => {
				if (!pendingFieldGroupUpdatesMap[key]) {
					delete mergedFieldGroupUpdatesMap[key];
				}
			});

			Object.keys(mergedFieldGroupUpdatesMap).forEach((fieldGroupLocator) => {
				mergedFieldGroupUpdates.push(
					mergedFieldGroupUpdatesMap[fieldGroupLocator],
				);
			});
		}
	});

	return mergedFieldGroupUpdates;
};

const mergeFieldGroupUpdates = (
	pendingFieldGroupUpdates: FieldGroupUpdateRequest[],
	existingFieldGroupUpdates: FieldGroupUpdateRequest[],
) => {
	const mergedFieldGroupUpdates = [] as FieldGroupUpdateRequest[];

	const pendingFieldGroupUpdatesMap = pendingFieldGroupUpdates
		? pendingFieldGroupUpdates.reduce((acc, curr) => {
				acc[curr.fieldGroupLocator] = curr;
				return acc;
		  }, {} as { [key: string]: FieldGroupUpdateRequest })
		: ({} as { [key: string]: FieldGroupUpdateRequest });

	const existingFieldGroupUpdatesMap = existingFieldGroupUpdates
		? existingFieldGroupUpdates.reduce((acc, curr) => {
				acc[curr.fieldGroupLocator] = curr;
				return acc;
		  }, {} as { [key: string]: FieldGroupUpdateRequest })
		: ({} as { [key: string]: FieldGroupUpdateRequest });

	const mergedFieldGroupUpdatesMap = {
		...existingFieldGroupUpdatesMap,
		...pendingFieldGroupUpdatesMap,
	};

	Object.keys(mergedFieldGroupUpdatesMap).forEach((fieldGroupLocator) => {
		mergedFieldGroupUpdates.push(mergedFieldGroupUpdatesMap[fieldGroupLocator]);
	});
	return mergedFieldGroupUpdates;
};

/**
 * This function adds updated field group fieldValues inside the matching fieldGroupsByLocator. The
 * field groups marked for removal will also be removed.
 */
export function consolidateFieldGroupsByLocator(
	fieldGroupsByLocator: FieldGroupsByLocatorMap,
	updatedFGs: FieldGroupUpdateRequest[],
	removedFGs: string[],
) {
	// update field groups
	updatedFGs?.forEach((fG) => {
		fieldGroupsByLocator[fG.fieldGroupLocator] = {
			...fieldGroupsByLocator[fG.fieldGroupLocator],
			...fG.fieldValues,
		};
	});

	removedFGs?.forEach((rFG) => {
		delete fieldGroupsByLocator[rFG];
	});

	return fieldGroupsByLocator;
}

const filterIndemnitiesForCreatePerilRequest = (
	indemnities: PerilCreateRequest & PerilCreateRequestHelpers,
) => {
	const filteredIndemnities = {
		...indemnities,
	} as PerilCreateRequest & PerilCreateRequestHelpers;
	if (indemnities.removeDeductible) {
		delete filteredIndemnities.removeDeductible;
		delete filteredIndemnities.deductible;
	}
	if (indemnities.removeIndemnityInAggregate) {
		delete filteredIndemnities.removeIndemnityInAggregate;
		delete filteredIndemnities.indemnityInAggregate;
	}
	if (indemnities.removeLumpSumPayment) {
		delete filteredIndemnities.removeLumpSumPayment;
		delete filteredIndemnities.lumpSumPayment;
	}
	if (indemnities.removeIndemnityPerEvent) {
		delete filteredIndemnities.removeIndemnityPerEvent;
		delete filteredIndemnities.indemnityPerEvent;
	}
	if (indemnities.removeIndemnityPerItem) {
		delete filteredIndemnities.removeIndemnityPerItem;
		delete filteredIndemnities.indemnityPerItem;
	}
	return filteredIndemnities;
};

export const getAllCharacteristicsFieldGroupLocators = (policy: Policy2) => {
	let result = {};

	if (!policy || !policy.characteristics?.length) return result;

	policy.characteristics.forEach((char) => {
		if (!isEmpty(char.fieldGroupsByLocator)) {
			result = {
				...result,
				...char.fieldGroupsByLocator,
			};
		}
	});

	return result;
};

export const getAllCharacteristicsFieldValues = (policy: Policy2) => {
	let result = {};

	if (!policy || !policy.characteristics?.length) return result;

	policy.characteristics.forEach((char) => {
		if (!isEmpty(char.fieldValues)) {
			result = {
				...result,
				...char.fieldValues,
			};
		}
	});

	return result;
};

export const getHasPaymentPluginEnabled = (
	productConfig: ProductConfigurationResponse,
) => {
	return !!productConfig?.policyConfiguration?.plugins?.createInstallments
		?.enabled;
};

export const getHasInputRepriceBehavior = (
	productConfig: ProductConfigurationResponse,
	endorsementResponse: EndorsementResponse,
) => {
	if (!productConfig?.policyConfiguration?.endorsements || !endorsementResponse)
		return false;

	const { endorsements } = productConfig.policyConfiguration;
	const endorsementConfig = endorsements.find(
		(e) => e.name === endorsementResponse.endorsementName,
	);
	return endorsementConfig.reprice && endorsementConfig.reprice === 'input';
};

export const isPaymentScheduleNamedChanged = (
	initialObj: RenewalResponse | EndorsementResponse,
	currentUpdateObj: RenewalResponse | EndorsementResponse,
	policy: Policy2,
) => {
	// initial value of `newPaymentScheduleName` can be from `initialObj.newPaymentScheduleName` or `policy.paymentScheduleName`
	// if there is `newPaymentScheduleName` in `initialObj` then compare it with `currentUpdateObj`
	if (initialObj && 'newPaymentScheduleName' in initialObj) {
		return (
			initialObj.newPaymentScheduleName !==
			currentUpdateObj?.newPaymentScheduleName
		);
	}
	// otherwise, compare `paymentScheduleName` of policy with `newPaymentScheduleName` in `currentUpdateObj` IF AND ONLY IF there is `newPaymentScheduleName` in `currentUpdateObj`
	if (currentUpdateObj && 'newPaymentScheduleName' in currentUpdateObj) {
		return (
			policy?.paymentScheduleName !== currentUpdateObj.newPaymentScheduleName
		);
	}

	return false;
};

export const endorsementHasUnsavedChanges = (
	initialEndorsementUpdateObj: EndorsementResponse,
	currEndorsementUpdateObj: EndorsementResponse,
	// isDirty and dirtyFields are from fastform's formState
	isDirty: boolean,
	dirtyFields: AnyObject,
	productConfig: ProductConfigurationResponse,
	policy: Policy2,
) => {
	// if form is dirty
	if (
		isDirty ||
		// isDirty will not work properly when checking dirty nested values
		// thus checking dirtyFields' keys
		!isEmpty(keys(dirtyFields))
	) {
		return true;
	}

	// if startTimestamp changed
	if (
		initialEndorsementUpdateObj?.startTimestamp !==
		currEndorsementUpdateObj?.startTimestamp
	)
		return true;

	// if reprice changed
	if (
		getHasInputRepriceBehavior(productConfig, initialEndorsementUpdateObj) &&
		initialEndorsementUpdateObj?.reprice !== currEndorsementUpdateObj?.reprice
	)
		return true;

	// if payment schedule name changed
	if (
		getHasPaymentPluginEnabled(productConfig) &&
		isPaymentScheduleNamedChanged(
			initialEndorsementUpdateObj,
			currEndorsementUpdateObj,
			policy,
		)
	)
		return true;

	if (
		initialEndorsementUpdateObj?.newPolicyEndTimestamp !==
		currEndorsementUpdateObj?.newPolicyEndTimestamp
	) {
		const initialNewPolicyEndTimestamp =
			initialEndorsementUpdateObj?.newPolicyEndTimestamp || '';
		if (
			initialNewPolicyEndTimestamp !==
			currEndorsementUpdateObj?.newPolicyEndTimestamp
		) {
			return true;
		}
	}

	return false;
};

export const renewalHasUnsavedChanges = (
	initialRenewalUpdateObj: RenewalResponse,
	currRenewalUpdateObj: RenewalResponse,
	// isDirty and dirtyFields are from fastform's formState
	isDirty: boolean,
	dirtyFields: AnyObject,
	productConfig: ProductConfigurationResponse,
	policy: Policy2,
) => {
	// if form is dirty
	if (
		isDirty ||
		// isDirty will not work properly when checking dirty nested values
		// thus checking dirtyFields' keys
		!isEmpty(keys(dirtyFields))
	) {
		return true;
	}

	if (
		initialRenewalUpdateObj?.overrideResourcesAsOfTimestamp !==
		currRenewalUpdateObj?.overrideResourcesAsOfTimestamp
	) {
		return true;
	}
	if (
		initialRenewalUpdateObj?.resetOverrideResourcesAsOfTimestamp !==
		currRenewalUpdateObj?.resetOverrideResourcesAsOfTimestamp
	) {
		return true;
	}

	// if endtimestamp changed
	if (
		formatDateStringShort(initialRenewalUpdateObj?.renewalEndTimestamp) !==
		formatDateStringShort(currRenewalUpdateObj?.renewalEndTimestamp)
	)
		return true;

	// if payment schedule name changed
	if (
		getHasPaymentPluginEnabled(productConfig) &&
		isPaymentScheduleNamedChanged(
			initialRenewalUpdateObj,
			currRenewalUpdateObj,
			policy,
		)
	) {
		return true;
	}

	return false;
};

export function getReferenceTimestampBasedOnState(
	transaction: RenewalResponse | EndorsementResponse,
) {
	return transaction.state === 'issued'
		? transaction.issuedTimestamp
		: transaction.state === 'quoted' || transaction.state === 'accepted'
		? transaction.updatedTimestamp
		: Date.now().toString();
}

export function filterForPolicySnapshot(
	createdTimestamp: string,
	replacedTimestamp: string,
	referenceTimestamp: string,
) {
	return (
		createdTimestamp < referenceTimestamp &&
		(!replacedTimestamp || replacedTimestamp >= referenceTimestamp)
	);
}
